import { useCallback, useEffect, useRef } from 'react';
import { prefersReducedMotion } from './tween-number';

const CONFETTI_COLORS = ['#38bdf8', '#7dd3fc', '#a5e8ff', '#0ea5e9', '#e0f2fe'];
const CONFETTI_COUNT = 28;
const CONFETTI_LIFETIME_MS = 800;
const FLOAT_LIFETIME_MS = 1050;
const BOUNCE_LIFETIME_MS = 850;

/**
 * Drives the navbar pill's earn-event choreography:
 *   1. Pill scale-bounce + amber glow (CSS animation).
 *   2. ~28 confetti DIVs fanning from the pill (diamond-blue palette to
 *      match the 💎 icon, against the pill's amber base).
 *   3. Floating "+N" numeral above the pill.
 *   4. Optional sound (B5 → E6 triangle blip via WebAudio).
 *
 * Teardown contract: every appended DOM node is tracked in a ref array
 * and removed on unmount; every setTimeout id is captured and cleared.
 * Reduced motion skips bounce/confetti/float — number tween still runs
 * in `applyBalance`, which lives in `ReputationPill.tsx`.
 */
export function useEarnChoreography(pillRef: React.RefObject<HTMLButtonElement | null>) {
  const nodesRef = useRef<Set<HTMLElement>>(new Set());
  const timeoutsRef = useRef<Set<number>>(new Set());
  const audioCtxRef = useRef<AudioContext | null>(null);

  const scheduleCleanup = useCallback((node: HTMLElement, lifetime: number) => {
    nodesRef.current.add(node);
    const id = window.setTimeout(() => {
      node.remove();
      nodesRef.current.delete(node);
      timeoutsRef.current.delete(id);
    }, lifetime);
    timeoutsRef.current.add(id);
  }, []);

  const playEarnSound = useCallback(() => {
    try {
      const AudioCtor: typeof AudioContext | undefined =
        typeof window === 'undefined'
          ? undefined
          : window.AudioContext ||
            (window as typeof window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!AudioCtor) return;
      if (!audioCtxRef.current) {
        audioCtxRef.current = new AudioCtor();
      }
      const ctx = audioCtxRef.current;
      const t0 = ctx.currentTime;
      const blip = (freq: number, t: number, dur: number, gainPeak: number) => {
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        o.type = 'triangle';
        o.frequency.value = freq;
        g.gain.setValueAtTime(0, t0 + t);
        g.gain.linearRampToValueAtTime(gainPeak, t0 + t + 0.005);
        g.gain.exponentialRampToValueAtTime(0.0001, t0 + t + dur);
        o.connect(g).connect(ctx.destination);
        o.start(t0 + t);
        o.stop(t0 + t + dur + 0.02);
      };
      blip(987.77, 0, 0.07, 0.14);
      blip(1318.5, 0.07, 0.18, 0.12);
    } catch {
      // Autoplay-policy denial is expected; reputation choreography never
      // surfaces audio failures because they are not user-actionable.
    }
  }, []);

  const celebrate = useCallback(
    (delta: number, options: { soundOn?: boolean } = {}) => {
      const pill = pillRef.current;
      if (!pill) return;

      pill.classList.remove('is-celebrating');
      void pill.offsetWidth;
      pill.classList.add('is-celebrating');
      const bounceId = window.setTimeout(() => {
        pill.classList.remove('is-celebrating');
        timeoutsRef.current.delete(bounceId);
      }, BOUNCE_LIFETIME_MS);
      timeoutsRef.current.add(bounceId);

      if (prefersReducedMotion()) return;

      const rect = pill.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;

      for (let i = 0; i < CONFETTI_COUNT; i++) {
        const node = document.createElement('div');
        node.className = 'rep-confetti';
        node.style.left = `${cx - 3.5}px`;
        node.style.top = `${cy - 4}px`;
        const angle = (Math.PI * 2 * i) / CONFETTI_COUNT + (Math.random() - 0.5) * 0.4;
        const dist = 60 + Math.random() * 50;
        node.style.setProperty('--dx', `${Math.cos(angle) * dist}px`);
        node.style.setProperty('--dy', `${Math.sin(angle) * dist}px`);
        node.style.setProperty('--rot', `${Math.random() * 720 - 360}deg`);
        node.style.background = CONFETTI_COLORS[i % CONFETTI_COLORS.length];
        document.body.appendChild(node);
        scheduleCleanup(node, CONFETTI_LIFETIME_MS);
      }

      const floatNode = document.createElement('div');
      floatNode.className = 'rep-float';
      floatNode.textContent = `+${delta}`;
      floatNode.style.left = `${cx}px`;
      floatNode.style.top = `${rect.top - 6}px`;
      document.body.appendChild(floatNode);
      scheduleCleanup(floatNode, FLOAT_LIFETIME_MS);

      if (options.soundOn) playEarnSound();
    },
    [pillRef, scheduleCleanup, playEarnSound]
  );

  useEffect(() => {
    const nodes = nodesRef.current;
    const timeouts = timeoutsRef.current;
    return () => {
      timeouts.forEach((id) => window.clearTimeout(id));
      timeouts.clear();
      nodes.forEach((node) => node.remove());
      nodes.clear();
      const ctx = audioCtxRef.current;
      if (ctx && ctx.state !== 'closed') {
        ctx.close().catch(() => {});
      }
      audioCtxRef.current = null;
    };
  }, []);

  return { celebrate };
}
